import { useCallback, useEffect, useRef, useState } from 'react';

export interface Kpis {
  chargersTotal: number;
  chargersOnline: number;
  availabilityPct: number;
  activeTransactions: number;
  energyLast24hWh: number;
  recentTransactions: Array<{
    ocppTransactionId: number;
    cpId: string;
    connectorId: number;
    idTag: string;
    startTime: string;
    stopTime: string | null;
    energyWh: number | null;
    stopReason: string | null;
  }>;
}

/**
 * Teto de espera de UMA leitura de KPI.
 *
 * Sem ele, `fetch` só termina quando a conexão termina — e uma fonte
 * **pendurada** (proxy que aceita e nunca responde, portal cativo,
 * balanceador engolindo) nunca vira falha. Medido: 20 segundos, 20
 * requisições emitidas, 20 em voo, nenhuma resolvida, e a tela exibindo o
 * número com borda sólida e sem aviso — a fonte parada há 20s e o
 * Dashboard apresentando o valor como atual.
 *
 * **5s, e não os 10s do `TIMEOUT_LEITURA_MS` do menu**, porque o
 * compromisso não é o mesmo. Lá o intervalo é de 60s e descartar uma
 * leitura lenta custa um minuto de atraso; aqui o intervalo é de **1s**, e
 * a leitura que demora já perdeu a utilidade — quando chegasse, seria
 * substituída pela próxima em seguida. Segurar é que sai caro: com polling
 * de 1s, cada segundo de teto é mais uma requisição empilhada em voo, e
 * mais um segundo de tela mentindo.
 *
 * Não menos que isso: 5s ainda é o dobro da pior latência plausível de uma
 * leitura útil (rede móvel ruim, ~2s), então lentidão normal não vira
 * falso "sem sinal" — que é justamente o erro que a degradação existe para
 * não cometer.
 */
export const TIMEOUT_LEITURA_KPI_MS = 5_000;

async function fetchKpis(): Promise<Kpis> {
  // `AbortController` + `setTimeout`, e não `AbortSignal.timeout`: o projeto
  // não define `build.target` (default `'modules'`, que inclui Safari 14,
  // sem `AbortSignal.timeout`), e relógio falso de teste não controla o
  // segundo, mas controla `setTimeout`. É o D-011, no mesmo repositório.
  const controle = new AbortController();
  const timer = setTimeout(() => controle.abort(), TIMEOUT_LEITURA_KPI_MS);
  try {
    const res = await fetch('/api/dashboard/kpis', {
      credentials: 'include',
      signal: controle.signal,
    });
    if (!res.ok) throw new Error(String(res.status));
    return (await res.json()) as Kpis;
  } catch (e) {
    // O `AbortError` do navegador tem mensagem em inglês e variável entre
    // motores, e essa mensagem chega ao `error` do hook — que a tela
    // classifica e, sem dado nenhum, exibe. `'timeout'` é estável, é
    // classificado como `sem-sinal` (não é `'401'`) e tem texto próprio em
    // `kpi-fonte.ts`.
    if (controle.signal.aborted) throw new Error('timeout');
    throw e;
  } finally {
    // Com polling de 1s, sem isto cada leitura boa deixaria um temporizador
    // de 5s para trás — um por segundo, por aba, para sempre.
    clearTimeout(timer);
  }
}

/** Exposto só para teste (mesmo padrão de `_internals` do resto do repo). */
export const _internals = { fetchKpis, TIMEOUT_LEITURA_KPI_MS };

/**
 * O que fazer com um frame de `/ws/dashboard`.
 *
 *  - `pong`     — é `{kind:'ping'}`: responder e **não** refrescar.
 *  - `invalidar`— é frame de dado (ou o `hello` de abertura): refrescar.
 *  - `ignorar`  — não é JSON, ou não tem `kind` de string, ou é um `pong`
 *                 ecoado. Nada a fazer.
 */
export type AcaoDeFrame = 'pong' | 'invalidar' | 'ignorar';

/**
 * Classifica um frame do canal. Exportado para teste — é aqui que mora a
 * regra, e testar o hook exigiria renderizador React (o pacote não tem jsdom).
 *
 * Por que parar de refrescar em TODO frame: até esta entrega o listener era
 * `() => void refresh()`, sem `JSON.parse`. Com o heartbeat de 30s que o canal
 * passou a ter, cada `ping` viraria um `GET /api/dashboard/kpis` a mais — e,
 * pior, o `ping` ficaria sem resposta e o servidor derrubaria o socket em 45s.
 *
 * Por que continuar sem ler o payload: o dado da tela vem sempre do `GET`, que
 * é escopado por garagem e gateado por `requireModule('dashboard')`. O push é
 * só invalidação de cache. Isso é deliberado — mantém o acoplamento mínimo e
 * faz o RBAC por módulo do servidor ser transparente para a tela: um perfil que
 * recebe menos frames apenas cai no polling, e nada de payload de WS é
 * renderizado.
 *
 * Frame sem `kind` de string é ignorado em vez de tratado como dado: todo
 * emissor do canal preenche `kind` (é o servidor que monta o envelope), então
 * um frame sem ele é ruído, não invalidação perdida.
 */
export function classificarFrameDoPainel(raw: string): AcaoDeFrame {
  let kind: unknown;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return 'ignorar';
    kind = (parsed as { kind?: unknown }).kind;
  } catch {
    return 'ignorar';
  }
  if (typeof kind !== 'string') return 'ignorar';
  if (kind === 'ping') return 'pong';
  // O servidor não ecoa `pong`; se algum dia ecoar, não é invalidação.
  if (kind === 'pong') return 'ignorar';
  // `hello` entra aqui de propósito: refrescar na abertura é o comportamento
  // que esta tela sempre teve.
  return 'invalidar';
}

/**
 * Monta o tratador de frame do canal.
 *
 * Existe como fábrica, e não inline no `useEffect`, para ser testável: o
 * pacote `web` roda vitest em ambiente node, sem jsdom nem renderizador React,
 * então um teste que precisasse montar o hook não conseguiria exercer o
 * `pong`. Com a fábrica, o teste dirige exatamente a função que o hook
 * instala como listener — sobra uma linha de fiação sem cobertura
 * (`addEventListener`), não a regra.
 */
export function criarTratadorDeFrameDoPainel(acoes: {
  responderPong: () => void;
  invalidar: () => void;
}): (raw: string) => void {
  return (raw: string) => {
    const acao = classificarFrameDoPainel(raw);
    if (acao === 'pong') return acoes.responderPong();
    if (acao === 'invalidar') return acoes.invalidar();
  };
}

/**
 * Assina o /ws/dashboard e refaz o fetch quando um evento chega.
 * Fallback curto para manter o painel operacional mesmo se o WebSocket cair.
 *
 * **Responder `pong` é obrigatório, não cosmético.** O canal entrou no registro
 * compartilhado de `ws-sessions.ts` e passou a ter heartbeat: `{kind:'ping'}` a
 * cada 30s, socket derrubado se não vier `{kind:'pong'}` em 45s. Este hook não
 * tem reconexão — o socket é criado uma vez no `useEffect` e nunca mais —,
 * então sem o `pong` todo socket de Dashboard morreria em 45s e não voltaria
 * enquanto a aba estivesse aberta. E a tela continuaria perfeita, porque o
 * polling de 1s abaixo cobre: o único sintoma seria o indicador `live` em
 * `false` para sempre. É a regressão silenciosa que a spec chama de mais grave
 * (R-3/C-1) — Dashboard em TV de pátio perde o push e ninguém abre bug.
 *
 * Reconexão continua **de fora** de propósito: o canal é aceleração de
 * sub-segundo sobre um polling de 1s, e a resposta certa para essa redundância
 * é consolidar os canais (follow-up F-1), não um segundo cliente WS com
 * backoff.
 */
export function useKpis() {
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  const refresh = useCallback(
    () =>
      fetchKpis()
        .then((k) => {
          setKpis(k);
          setError(null);
        })
        .catch((e: Error) => setError(e.message)),
    [],
  );

  useEffect(() => {
    void refresh();
    // A especificação do painel exige leitura em cadência de um segundo.
    // Com WebSocket ativo, os eventos normalmente antecipam essa atualização.
    const poll = setInterval(() => void refresh(), 1_000);

    // tenta WS
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${proto}//${window.location.host}/ws/dashboard`);
    wsRef.current = ws;
    ws.addEventListener('open', () => setLive(true));
    ws.addEventListener('close', () => setLive(false));
    ws.addEventListener('error', () => setLive(false));
    const tratarFrame = criarTratadorDeFrameDoPainel({
      responderPong: () => {
        try {
          ws.send(JSON.stringify({ kind: 'pong' }));
        } catch {
          /* socket já fechando: o servidor derruba pelo prazo de pong */
        }
      },
      invalidar: () => void refresh(),
    });
    ws.addEventListener('message', (ev: MessageEvent) => tratarFrame(String(ev.data)));

    return () => {
      clearInterval(poll);
      ws.close();
    };
  }, [refresh]);

  return { kpis, error, live, refresh };
}
